import React from 'react'
import { useHistory } from 'react-router-dom'

import useContainerContext from '../../containerContext'
import { useApi, useBreakpoint } from '../../../hooks'
import toast from '../../toast'
import { formatToLocalTime } from '../../../utils/dates'
import Table, { Column } from '../../table'
import { SortableColumn } from '../../table-plugins/sortable'
import AvatarName from '../../avatarName'
import VehicleSummary from '../../vehicle/VehicleSummary'
import { isReportProcessing, isReportFailed } from './reportStatus'
// TODO before release: swap for the high-quality ADAS Map logo file (this PNG is a
// 599x130 stand-in for local testing — see the AB#38249 release checklist).
import adasMapLogo from '../../../assets/images/adas-map-logo.png'
import { showNeedsAttention } from '../../../utils/adasNeedsAttention'
import { openSignedUrl } from '../../../utils/openSignedUrl'

const ProcessingRow = (props) => {
  const { rowdata, onClick, role, className = '', ...rest } = props
  const loading = isReportProcessing(rowdata)
  const failed = isReportFailed(rowdata)
  const inactive = loading || failed
  const stateClass = loading ? 'adas-report-row--loading' : failed ? 'adas-report-row--failed' : ''
  return (
    <tr
      {...rest}
      role={inactive ? undefined : role}
      onClick={inactive ? undefined : onClick}
      className={`${className} ${stateClass}`.trim()}
    />
  )
}

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null
}

const getWorkflowBadgeStyle = (stage) => {
  if (!stage || !stage.color) return null
  const rgb = hexToRgb(stage.color)
  if (!rgb) return null
  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
    color: stage.color,
  }
}

const getWorkflowBadgeClass = (stage) => {
  if (!stage || !stage.name) return 'default'
  const name = stage.name.toLowerCase()
  if (name === 'added') return 'added'
  if (name === 'scheduled') return 'scheduled'
  if (name.includes('dropped') || name.includes('drop')) return 'dropped-off'
  if (name === 'complete' || name === 'completed') return 'complete'
  return 'default'
}

const sourceLabels = {
  Internal: 'Internal',
  manual: 'Internal',
  CCC: 'CCC',
  adasThink: 'CCC',
  Client: 'Client',
  'Client Portal': 'Client',
  'client-portal': 'Client',
  'client-portal-upload': 'Client',
}

// Built from an uploaded ADAS Map (OPUS) report rather than a repair estimate.
const isAdasMapReport = (report) =>
  !!(report && report.sourceDocument && report.sourceDocument.type === 'adas-map')

// A failed row's "View uploaded estimate" link (2026-10-04): the PDF the user
// uploaded, so they can see which estimate failed and re-upload it. Rendered
// only when the list says the API can resolve a file for the report — older
// failed rows may have none, and show the failure text alone.
const UploadedEstimateLink = ({ report, onOpen }) => {
  if (!report.hasSourceDocument) return null
  return (
    <div>
      {/* A link by design (AB#40423 AC4): it opens the PDF in a
          new tab. The href is a placeholder because the URL is signed on
          click, behind the session. */}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href='#'
        className='adas-report-failed-link'
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onOpen(report)
        }}
      >
        View uploaded estimate
      </a>
    </div>
  )
}

const ReportTable = () => {
  const { data = [], state, setState, pricingByVendor = {}, answerByClient = {} } = useContainerContext()
  const { BREAKPOINT, screenBiggerThan } = useBreakpoint()
  const isBiggerThanMD = screenBiggerThan(BREAKPOINT.MD)
  const history = useHistory()
  const { adasReportService } = useApi()

  // The URL is signed on demand (it expires in minutes), so the link fetches
  // it on click and opens the PDF in a new tab opened inside the click.
  const openUploadedEstimate = (report) => {
    openSignedUrl(() => adasReportService.uploadedEstimate(report._id).then((res) => res && res.url))
      .catch((err) => {
        toast.error({
          title: 'Uploaded estimate',
          body: (err && err.message) || 'Could not open the uploaded estimate. Please try again.',
        })
      })
  }

  const canViewPrice = (report) => {
    const vendorId = report.company && (report.company._id || report.company)
    if (!vendorId) return false
    return !!pricingByVendor[vendorId.toString()]
  }

  if (!data.length) {
    return <p className='adas-empty'>No reports found.</p>
  }

  const goToReport = (report) => {
    if (isReportProcessing(report) || isReportFailed(report)) return
    history.push('/adas-reports/' + report._id)
  }
  const getVendorName = report => (report.vendor && report.vendor.name) || (report.company && report.company.name) || '—'

  // Mobile: card-based layout
  if (!isBiggerThanMD) {
    return (
      <div>
        {data.map(report => {
          const loading = isReportProcessing(report)
          const failed = isReportFailed(report)
          const inactive = loading || failed
          const v = report.vehicle || {}
          const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ') || '—'
          const vendorName = getVendorName(report)
          const isNew = !report.lastViewedAt
          const stage = report.workflowStage

          return (
            <div
              key={report._id}
              className={`adas-mobile-card${loading ? ' is-processing' : ''}${failed ? ' is-failed' : ''}`}
              onClick={() => { if (!inactive) goToReport(report) }}
            >
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex align-items-center gap-2'>
                  {/* Always occupy the dot's slot so the avatar and name line up
                      whether or not the row is new (AB#40422 AC6). */}
                  <span className={`unread-badge${!inactive && isNew ? '' : ' unread-badge--placeholder'}`} />
                  <AvatarName size='small' value={vendorName} className='me-2' />
                  <strong style={{ fontSize: 14, color: '#2A2A2A' }}>{vendorName}</strong>
                </div>
                <small style={{ color: '#AAAAAA' }}>{formatToLocalTime(report.dateCreated)}</small>
              </div>
              {/* Report number (#37247 parity pass) -- mobile-card equivalent
                  of the new desktop Report # column. */}
              {report.number != null && (
                <div style={{ marginTop: 4 }}>
                  <span className='adas-report-number-tag'>{report.number}</span>
                  {/* The mobile card shows no source line, so the tag sits here. */}
                  {!inactive && isAdasMapReport(report) && (
                    <img className='adas-map-logo' style={{ marginLeft: 8, verticalAlign: 'middle' }} src={adasMapLogo} alt='ADAS Map' />
                  )}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12, color: failed ? '#C0392B' : '#2A2A2A' }}>
                {loading
                  ? 'Generating ADAS report. Please wait up to one minute.'
                  : failed
                    ? 'Report generation failed. Please try uploading again.'
                    : vehicle}
              </div>
              {failed && <UploadedEstimateLink report={report} onOpen={openUploadedEstimate} />}
              <div className='d-flex justify-content-between align-items-center mt-2'>
                <span style={{ fontSize: 14 }}>
                  {!inactive && canViewPrice(report) && report.totalPrice != null
                    ? `$${Number(report.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                  {/* Corrected copy: Figma reads "N Ops", not "N service(s)"
                      -- matches the admin-portal fix (#37247 parity pass). */}
                  {!inactive && !!(report.serviceCounts && report.serviceCounts.total) && (
                    <span className='adas-report-service-count-tag'>
                      {report.serviceCounts.total} Ops
                    </span>
                  )}
                </span>
                {!inactive && showNeedsAttention(report, answerByClient) && (
                  <span className='adas-needs-attention-tag adas-needs-attention-tag--inline'>Needs Attention</span>
                )}
                {(loading || failed || (stage && stage.name)) && (
                  <span
                    className={`workflow-badge ${failed ? 'dropped-off' : 'default'}`}
                    style={!inactive ? (getWorkflowBadgeStyle(stage) || undefined) : undefined}
                  >
                    {loading ? 'Generating' : failed ? 'Failed' : stage.name}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Desktop: shared Table component (matches estimates/invoices pattern)
  return (
    <Table
      data={data}
      rowKey='_id'
      state={state}
      setState={setState}
      className='primary'
      borderless
      onRowClick={goToReport}
      rowComponent={ProcessingRow}
    >
      <Column
        dataKey='vendor.name'
        width='14%'
        header={<u>Vendor</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) {
            return (
              <div className='d-flex align-items-center adas-report-loading-cell'>
                <span className='adas-report-loading-dot me-2' />
                <div className='adas-report-loading-copy'>
                  <div className='adas-report-loading-title'>Processing report...</div>
                  <div className='adas-report-loading-subtitle'>This row will be clickable when generation is complete.</div>
                </div>
              </div>
            )
          }
          if (isReportFailed(rowData)) {
            return (
              <div className='d-flex align-items-center adas-report-failed-cell'>
                <div className='adas-report-loading-copy'>
                  <div className='adas-report-loading-title'>Report generation failed</div>
                  <div className='adas-report-loading-subtitle'>adasThink couldn't process this estimate. Please try uploading again.</div>
                  <UploadedEstimateLink report={rowData} onOpen={openUploadedEstimate} />
                </div>
              </div>
            )
          }
          const vendorName = value || getVendorName(rowData)
          const isNew = !rowData.lastViewedAt
          return (
            <div className='d-flex align-items-center'>
              {/* The slot is always there: a new row's dot must not push the
                  avatar and name right of the rows around it. */}
              <span className={`unread-badge me-2${isNew ? '' : ' unread-badge--placeholder'}`} />
              <AvatarName size='medium' value={vendorName} className='me-2' />
              <div>{vendorName}</div>
            </div>
          )
        }}
      />
      <SortableColumn
        dataKey='dateCreated'
        width='8%'
        headerLabel={<u>Date</u>}
        render={value => formatToLocalTime(value)}
      />
      {/* Report number (#37247 parity pass, admin equivalent: AdasReports.js).
          Bare number in a gray chip, matching the admin list treatment and
          the detail header (VehicleHeader.js already renders "ADAS Report
          #N") -- this was the one list surface still missing it. */}
      <Column
        dataKey='number'
        width='9%'
        header={<u>Report #</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          // Same red tag the admin list shows, under the number, but only when this
          // client can answer the questions behind it (see utils/adasNeedsAttention).
          const attention = !isReportFailed(rowData) && showNeedsAttention(rowData, answerByClient)
          return (
            <div>
              {value != null ? <span className='adas-report-number-tag'>{value}</span> : '—'}
              {attention && <div><span className='adas-needs-attention-tag'>Needs Attention</span></div>}
            </div>
          )
        }}
      />
      <Column
        dataKey='checkIn.insurance.name'
        width='8%'
        header={<u>Insurance</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const insuranceName = value || (rowData.checkIn && rowData.checkIn.insurance && rowData.checkIn.insurance.name)
          return insuranceName || '—'
        }}
      />
      <Column
        dataKey='source'
        width='7%'
        className='text-center'
        header={<u>Source</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const label = sourceLabels[value] || value || '—'
          // Built from an uploaded ADAS Map (OPUS) report: tagged under the source.
          if (!isAdasMapReport(rowData)) return label
          return (
            <>
              {label}
              <div><img className='adas-map-logo' src={adasMapLogo} alt='ADAS Map' /></div>
            </>
          )
        }}
      />
      <Column
        dataKey='vehicle'
        width='25%'
        header={<u>Vehicle Info</u>}
        className='text-center'
        render={(v, { rowData }) => {
          if (isReportProcessing(rowData) || isReportFailed(rowData)) return '—'
          if (!v) return '—'
          return (
            <VehicleSummary
              vehicle={v}
              className='adas-report-vehicle-cell'
            />
          )
        }}
      />
      <SortableColumn
        dataKey='totalPrice'
        width='7%'
        headerLabel={<u>Estimated Total</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const counts = rowData.serviceCounts
          // Rolled-up service count on a secondary line under the total, per
          // the design (#37241). Non-clickable — a label, not a control. It is
          // NOT gated on canViewPrice: a count is not a price, so a client
          // without pricing visibility still sees how much work is on the
          // report. Served on the list response, so no extra round-trip.
          // Corrected copy: Figma reads "N Ops", not "N service(s)" -- matches
          // the admin-portal fix (#37247 parity pass).
          const countTag = counts && counts.total
            ? (
              <span className='adas-report-service-count-tag'>
                {counts.total} Ops
              </span>
              )
            : null
          const price = !canViewPrice(rowData) || (!value && value !== 0)
            ? '—'
            : `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          if (!countTag) return price
          return (
            <span className='adas-report-total-cell'>
              <span>{price}</span>
              {countTag}
            </span>
          )
        }}
      />
      <Column
        dataKey='clientPortal.calibrationReady'
        width='9%'
        header={<u>Calibration Status</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const calDate = rowData.clientPortal && rowData.clientPortal.calibrationReadyDate
          if (value === 'yes') return <div><div>READY</div>{calDate && <div style={{ fontSize: 12, color: '#5D696F' }}>{formatToLocalTime(calDate)}</div>}</div>
          if (value === 'no') return 'NOT READY'
          return '—'
        }}
      />
      <Column
        dataKey='workflowStage'
        width='9%'
        header={<u>Workflow Status</u>}
        className='text-center'
        render={(stage, { rowData }) => {
          if (isReportProcessing(rowData)) {
            return <span className='workflow-badge default'>Generating</span>
          }
          if (isReportFailed(rowData)) {
            return <span className='workflow-badge dropped-off'>Failed</span>
          }
          if (!stage || !stage.name) return '—'
          const dynamicStyle = getWorkflowBadgeStyle(stage)
          return (
            <span
              className={`workflow-badge${!dynamicStyle ? ' ' + getWorkflowBadgeClass(stage) : ''}`}
              style={dynamicStyle || undefined}
            >
              {stage.name}
            </span>
          )
        }}
      />
    </Table>
  )
}

export default ReportTable
